import { textLayoutAtLeast, TEXT_LAYOUT_RUN_FONTS } from './constants';

/**
 * A font as a box or a run names it: font name, weight label (`Thin` … `Black`, maybe with a width word) and slant;
 * absent — not set.
 */
export interface FaceStyle {
  name?: string;
  fontWeight?: string;
  italic?: boolean;
}

/** The face of a run that does not draw with its box face. */
export interface RunFace {
  /** The run's own font name, when it differs from the box's; absent — the run is in the box font, at its own weight or slant. */
  ownName?: string;
  /** The run's weight label, else the box's. */
  fontWeight?: string;
  /** The run's slant, else the box's (`false` on the run is its own: an upright run in an italic box). */
  italic?: boolean;
}

const nonEmpty = (value: string | undefined): string | undefined => (value?.trim() ? value : undefined);

/** The weight label and slant a run asks for: its own when set (a blank weight is unset), else the box's. */
export function runFontStyle(box: FaceStyle, run: FaceStyle): Pick<RunFace, 'fontWeight' | 'italic'> {
  return { fontWeight: nonEmpty(run.fontWeight) ?? box.fontWeight, italic: run.italic ?? box.italic };
}

/**
 * The face a run draws with from {@link TEXT_LAYOUT_RUN_FONTS} on, when it is not the box face: its font name differs
 * from the box's, or its weight or slant does. `undefined` — the run draws with the box face (an older box, or a run
 * that changes none of the three). The box name is its PostScript name, else its font name. Mirrors
 * `run_style::run_face` (Rust); parity/fixtures/render-rule/render-rule.json (`runFaceVectors`) referees both.
 *
 * Read under the existing run-fonts step on purpose — no new step and no render-rule version bump: no stored model
 * carried a run weight before this rule, and the only run slant set apart from its box sits on a box below that step.
 */
export function runFace(textLayout: number | undefined, box: FaceStyle, run: FaceStyle): RunFace | undefined {
  if (!textLayoutAtLeast(textLayout, TEXT_LAYOUT_RUN_FONTS)) return undefined;
  const runName = nonEmpty(run.name);
  const ownName = runName !== box.name ? runName : undefined;
  const { fontWeight, italic } = runFontStyle(box, run);
  const ownWeight = fontWeight?.trim() !== box.fontWeight?.trim();
  const ownSlant = !!italic !== !!box.italic;

  return ownName || ownWeight || ownSlant ? { ownName, fontWeight, italic } : undefined;
}

/**
 * The named instance a run's own face is looked up by: `own` — the run's own name; `box` — the box's PostScript name,
 * for a run that changes only the slant (a PSD box keeps its weight in that name, its weight label stays `Regular`),
 * the slant going on top of the instance; `none` — the run sets a weight of its own, and the box instance would pin
 * the box weight over it. Mirrors `run_style::run_instance` (Rust); `runFaceVectors` referee both.
 */
export type RunInstance = 'own' | 'box' | 'none';

/** Which name a run's own face asks an instance by ({@link RunInstance}). */
export function runInstance(box: FaceStyle, face: RunFace): RunInstance {
  if (face.ownName) return 'own';
  return face.fontWeight?.trim() === box.fontWeight?.trim() ? 'box' : 'none';
}

/**
 * True for an upright run on the box instance in an italic box: the run takes the box instance's other axes (its weight
 * among them) but none of its slant — `uprightOf` (psd-render-wasm font-instances.ts) sets `ital` and `slnt` back to 0.
 * Mirrors `run_style::run_drops_box_slant` (Rust); `runFaceVectors` (`upright`) referee both.
 */
export function runDropsBoxSlant(box: FaceStyle, face: RunFace): boolean {
  return runInstance(box, face) === 'box' && !face.italic && !!box.italic;
}
